import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { scrollToTop } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BlogPost, BlogCategory } from "@shared/schema";
import { 
  Search, 
  Calendar, 
  Clock, 
  TrendingUp, 
  Tag, 
  Filter, 
  BookOpen,
  ArrowRight
} from "lucide-react";

export default function BlogPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    scrollToTop();
  }, []);

  // Fetch blog posts
  const { 
    data: posts = [], 
    isLoading: postsLoading 
  } = useQuery<BlogPost[]>({
    queryKey: ["/api/blog/posts"],
  });

  // Fetch blog categories
  const { 
    data: categories = [], 
    isLoading: categoriesLoading 
  } = useQuery<BlogCategory[]>({
    queryKey: ["/api/blog/categories"],
  });

  // Fetch featured posts
  const { 
    data: featuredPosts = [], 
    isLoading: featuredLoading 
  } = useQuery<BlogPost[]>({
    queryKey: ["/api/blog/featured"],
  });

  // Apply filters when search query or active category changes
  useEffect(() => {
    let filtered = [...posts];
    
    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.keywords?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Filter by category
    if (activeCategory !== 'all') {
      filtered = filtered.filter(post => 
        post.categoryId && post.categoryId.toString() === activeCategory
      );
    }
    
    setFilteredPosts(filtered);
  }, [searchQuery, activeCategory, posts]);

  // Helper function to format dates
  const formatDate = (dateString: string | Date | null) => {
    if (!dateString) return "Date unavailable";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get category name by ID
  const getCategoryName = (categoryId: number | null) => {
    if (!categoryId) return "Uncategorized";
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : "Uncategorized";
  };

  return (
    <>
      <div className="pt-24 pb-12 bg-[#f8f9fa]">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#1a365d] mb-4">
              Accounting & Finance Insights Blog
            </h1>
            <div className="w-24 h-1 bg-[#7d9b76] mx-auto mb-6"></div>
            <p className="text-lg text-gray-700">
              Expert advice, resources, and insights about accounting, bookkeeping, tax planning, and financial management for businesses in Draper, Utah and nationwide.
            </p>
          </div>
        </div>
      </div>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          {featuredPosts.length > 0 && !featuredLoading && (
            <div className="mb-16">
              <h2 className="font-serif text-3xl font-bold text-[#1a365d] mb-8 text-center">Featured Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {featuredPosts.slice(0, 3).map(post => (
                  <div key={post.id} className="group relative rounded-lg overflow-hidden shadow-lg transition-transform hover:-translate-y-1 duration-300">
                    <div className="h-52 overflow-hidden">
                      {post.imageUrl ? (
                        <img 
                          src={post.imageUrl} 
                          alt={post.imageAlt || post.title} 
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-r from-[#1a365d] to-[#7d9b76] flex items-center justify-center">
                          <BookOpen className="h-16 w-16 text-white/70" />
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex items-center mb-3">
                        <Badge variant="outline" className="bg-[rgba(125,155,118,0.1)] text-[#7d9b76] hover:bg-[rgba(125,155,118,0.2)]">
                          {getCategoryName(post.categoryId)}
                        </Badge>
                        <div className="ml-auto flex items-center text-sm text-gray-500">
                          <Clock className="h-4 w-4 mr-1" />
                          <span>{post.readTime} min read</span>
                        </div>
                      </div>
                      <h3 className="font-serif text-xl font-bold text-[#1a365d] mb-3 group-hover:text-[#7d9b76] transition-colors">
                        <Link href={`/blog/${post.slug}`} className="block">
                          {post.title}
                        </Link>
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-2">
                        {post.summary}
                      </p>
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="h-4 w-4 mr-1" />
                        <span>{formatDate(post.publishedAt)}</span>
                      </div>
                    </div>
                    <Link href={`/blog/${post.slug}`} className="absolute inset-0">
                      <span className="sr-only">Read {post.title}</span>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mb-12">
            <div className="flex flex-col md:flex-row gap-4 items-center mb-8">
              <div className="w-full md:w-2/3 relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input 
                  placeholder="Search for accounting tips, tax advice, financial guidance..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12"
                />
              </div>
              <div className="w-full md:w-1/3">
                <div className="flex items-center bg-[#f8f9fa] p-2 rounded-md">
                  <Filter className="h-4 w-4 text-gray-500 mr-2" />
                  <select 
                    className="bg-transparent flex-1 focus:outline-none"
                    value={activeCategory}
                    onChange={(e) => setActiveCategory(e.target.value)}
                  >
                    <option value="all">All Categories</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id.toString()}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <Tabs defaultValue="latest" className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <TabsList>
                  <TabsTrigger value="latest" className="text-sm">Latest</TabsTrigger>
                  <TabsTrigger value="popular" className="text-sm">Most Popular</TabsTrigger>
                </TabsList>
                <Link href="/blog/categories" className="text-sm text-[#7d9b76] hover:text-[#1a365d] flex items-center gap-1">
                  View all categories <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
              
              <Separator className="mb-8" />
              
              <TabsContent value="latest" className="mt-0">
                {postsLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {[...Array(6)].map((_, index) => (
                      <div key={index} className="bg-gray-50 animate-pulse rounded-lg h-96"></div>
                    ))}
                  </div>
                ) : filteredPosts.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredPosts.map(post => (
                      <div key={post.id} className="bg-white border border-gray-100 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                        <div className="h-48 relative">
                          {post.imageUrl ? (
                            <img 
                              src={post.imageUrl} 
                              alt={post.imageAlt || post.title} 
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-r from-[#1a365d]/10 to-[#7d9b76]/10 flex items-center justify-center">
                              <BookOpen className="h-12 w-12 text-[#1a365d]/20" />
                            </div>
                          )}
                          <Badge className="absolute top-3 left-3 bg-[#1a365d]">
                            {getCategoryName(post.categoryId)}
                          </Badge>
                        </div>
                        <div className="p-5">
                          <div className="flex items-center text-sm text-gray-500 mb-3">
                            <Calendar className="h-4 w-4 mr-1" />
                            <span className="mr-3">{formatDate(post.publishedAt)}</span>
                            <Clock className="h-4 w-4 mr-1" />
                            <span>{post.readTime} min read</span>
                          </div>
                          <h3 className="font-serif text-xl font-bold text-[#1a365d] mb-3 hover:text-[#7d9b76] transition-colors">
                            <Link href={`/blog/${post.slug}`} className="block">
                              {post.title}
                            </Link>
                          </h3>
                          <p className="text-gray-600 mb-4 line-clamp-2">
                            {post.summary}
                          </p>
                          <Link 
                            href={`/blog/${post.slug}`}
                            className="inline-flex items-center text-[#7d9b76] hover:text-[#1a365d] font-medium transition-colors"
                          >
                            Read More <ArrowRight className="ml-2 h-4 w-4" />
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="mb-4 text-gray-400">
                      <Search className="h-12 w-12 mx-auto" />
                    </div>
                    <h3 className="text-xl font-serif font-bold text-[#1a365d] mb-2">No articles found</h3>
                    <p className="text-gray-600 mb-4">
                      Try adjusting your search or filter criteria
                    </p>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setSearchQuery("");
                        setActiveCategory("all");
                      }}
                    >
                      Reset Filters
                    </Button>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="popular" className="mt-0">
                {postsLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {[...Array(6)].map((_, index) => (
                      <div key={index} className="bg-gray-50 animate-pulse rounded-lg h-96"></div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {[...posts]
                      .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
                      .slice(0, 9)
                      .map(post => (
                        <div key={post.id} className="bg-white border border-gray-100 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                          <div className="h-48 relative">
                            {post.imageUrl ? (
                              <img 
                                src={post.imageUrl} 
                                alt={post.imageAlt || post.title} 
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-r from-[#1a365d]/10 to-[#7d9b76]/10 flex items-center justify-center">
                                <BookOpen className="h-12 w-12 text-[#1a365d]/20" />
                              </div>
                            )}
                            <Badge className="absolute top-3 left-3 bg-[#1a365d]">
                              {getCategoryName(post.categoryId)}
                            </Badge>
                            <div className="absolute top-3 right-3 flex items-center bg-white rounded-full px-2 py-1 text-xs font-medium text-[#1a365d]">
                              <TrendingUp className="h-3 w-3 mr-1 text-[#7d9b76]" />
                              {post.viewCount} views
                            </div>
                          </div>
                          <div className="p-5">
                            <div className="flex items-center text-sm text-gray-500 mb-3">
                              <Calendar className="h-4 w-4 mr-1" />
                              <span className="mr-3">{formatDate(post.publishedAt)}</span>
                              <Clock className="h-4 w-4 mr-1" />
                              <span>{post.readTime} min read</span>
                            </div>
                            <h3 className="font-serif text-xl font-bold text-[#1a365d] mb-3 hover:text-[#7d9b76] transition-colors">
                              <Link href={`/blog/${post.slug}`} className="block">
                                {post.title}
                              </Link>
                            </h3>
                            <p className="text-gray-600 mb-4 line-clamp-2">
                              {post.summary}
                            </p>
                            <Link 
                              href={`/blog/${post.slug}`}
                              className="inline-flex items-center text-[#7d9b76] hover:text-[#1a365d] font-medium transition-colors"
                            >
                              Read More <ArrowRight className="ml-2 h-4 w-4" />
                            </Link>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          <div className="text-center">
            <Button 
              className="bg-[#1a365d] hover:bg-opacity-90"
              size="lg"
            >
              Load More Articles
            </Button>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#f8f9fa]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-serif text-3xl font-bold text-[#1a365d] mb-4">
                Popular Categories
              </h2>
              <p className="text-gray-600">
                Browse our articles by topic to find the financial guidance you need
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {categoriesLoading ? (
                [...Array(8)].map((_, index) => (
                  <div 
                    key={index} 
                    className="bg-white rounded-lg p-6 text-center animate-pulse h-32 flex items-center justify-center"
                  >
                    <div className="bg-gray-200 h-6 w-24 rounded mx-auto"></div>
                  </div>
                ))
              ) : (
                categories.slice(0, 8).map(category => (
                  <Link 
                    key={category.id}
                    href={`/blog/category/${category.slug}`}
                    className="bg-white rounded-lg p-6 text-center shadow-sm hover:shadow-md transition-shadow border border-gray-100 hover:border-[#7d9b76]/20"
                  >
                    <h3 className="font-serif font-bold text-[#1a365d] hover:text-[#7d9b76] transition-colors">
                      {category.name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {posts.filter(post => post.categoryId === category.id).length} articles
                    </p>
                  </Link>
                ))
              )}
            </div>
            
            {categories.length > 8 && (
              <div className="text-center mt-8">
                <Link 
                  href="/blog/categories"
                  className="inline-flex items-center text-[#7d9b76] hover:text-[#1a365d] font-medium transition-colors"
                >
                  View All Categories <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="bg-[#1a365d] text-white rounded-lg p-8 md:p-12 text-center">
            <h2 className="font-serif text-3xl font-bold mb-4">
              Have a Question About Your Finances?
            </h2>
            <p className="max-w-2xl mx-auto mb-6">
              Our team of accounting experts is here to help you navigate your financial journey with confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                href="/contact"
                className="bg-white text-[#1a365d] hover:bg-gray-100 px-6 py-3 rounded-md font-semibold"
              >
                Contact Us
              </Link>
              <Link 
                href="/booking"
                className="bg-[#7d9b76] text-white hover:bg-opacity-90 px-6 py-3 rounded-md font-semibold"
              >
                Schedule a Consultation
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}